import { isNil } from 'lodash-es';

import type {
  Timeseries,
  TimeseriesDatapoint,
} from '../../../types/timeseries.js';
import { getDatapointIsGap } from '../../../utils/get-data-point-is-gap.js';
import {
  SparklineDatapointMetadata,
  SparklineGapPolicy,
} from '../types/sparkline-props.js';
import { SparklineXAxisInternalProps } from '../types/sparkline-x-axis-props.js';

interface XAxisBounds {
  min: Date;
  max: Date;
}

/** Checks whether a datapoint falls within the configured x-axis boundaries. */
function isInXAxisBounds(
  datapoint: TimeseriesDatapoint,
  xAxis: SparklineXAxisInternalProps,
  customMin: boolean,
  customMax: boolean,
): boolean {
  if (customMin && datapoint.start < xAxis.min) {
    return false;
  }
  return !(customMax && datapoint.start > xAxis.max);
}

/**
 * Starting from `startIndex`, returns the index of the first datapoint
 * whose value is not nil, or `datapoints.length` if none is found.
 */
function findNextDefinedIndex(
  datapoints: TimeseriesDatapoint[],
  startIndex: number,
): number {
  let i = startIndex;
  while (i < datapoints.length && isNil(datapoints[i].value)) {
    i++;
  }
  return i;
}

/** Updates the tracked x-axis bounds with the current datapoint's timestamps. */
function updateXAxisBounds(
  datapoint: TimeseriesDatapoint,
  xAxisBounds: XAxisBounds,
  customMin: boolean,
  customMax: boolean,
): void {
  if (customMin && datapoint.start < xAxisBounds.min) {
    xAxisBounds.min = datapoint.start;
  }
  if (customMax) {
    const endDate = datapoint.end ?? datapoint.start;
    if (endDate > xAxisBounds.max) {
      xAxisBounds.max = endDate;
    }
  }
}

/**
 * If there is a gap between the current datapoint and the next defined one,
 * appends a gap-marker datapoint. Nil-value datapoints in between are
 * naturally skipped by the caller's existing nil check.
 */
function processGap(
  datapoints: TimeseriesDatapoint[],
  currentIndex: number,
  currentDatapoint: TimeseriesDatapoint,
  sanitizedDatapoints: SparklineDatapointMetadata[],
): void {
  const nextIndex = findNextDefinedIndex(datapoints, currentIndex + 1);
  const nextDatapoint = datapoints[nextIndex];

  if (nextDatapoint) {
    const isGap = getDatapointIsGap(currentDatapoint, nextDatapoint);
    if (isGap) {
      sanitizedDatapoints.push({
        start: currentDatapoint.end ?? currentDatapoint.start,
        end: nextDatapoint.start,
        value: Infinity,
        isGap: true,
      });
    }
  }
}

function createBoundaryDatapoint(date: Date): SparklineDatapointMetadata {
  return { start: new Date(date), value: Infinity, isGap: true };
}

/** Prepends/appends gap-marker datapoints when the data doesn't cover the full x-axis range. */
function addXAxisBoundaryDatapoints(
  sanitizedDatapoints: SparklineDatapointMetadata[],
  xAxis: SparklineXAxisInternalProps,
  xAxisBounds: XAxisBounds,
  customMin: boolean,
  customMax: boolean,
): void {
  if (customMin && xAxisBounds.min > xAxis.min) {
    sanitizedDatapoints.unshift(createBoundaryDatapoint(xAxis.min as Date));
  }
  if (customMax && xAxisBounds.max < xAxis.max) {
    sanitizedDatapoints.push(createBoundaryDatapoint(xAxis.max as Date));
  }
}

export const sanitizeSparklineData = (
  series: Timeseries,
  gapPolicy: SparklineGapPolicy,
  xAxis: SparklineXAxisInternalProps,
) => {
  const domainValues = { min: Infinity, max: -Infinity };

  if (isNil(series) || !series.datapoints?.length) {
    return { sanitizedSeries: { datapoints: [], name: '' }, domainValues };
  }

  const customXAxisMin = xAxis.min !== 'data-min';
  const customXAxisMax = xAxis.max !== 'data-max';
  const sanitizedDatapoints: SparklineDatapointMetadata[] = [];
  const xAxisBounds: XAxisBounds = {
    min: series.datapoints[0].start,
    max: series.datapoints[0].start,
  };

  for (let i = 0; i < series.datapoints.length; i++) {
    const currentDatapoint = series.datapoints[i];

    if (isNil(currentDatapoint.value)) {
      continue;
    }

    if (
      !isInXAxisBounds(currentDatapoint, xAxis, customXAxisMin, customXAxisMax)
    ) {
      continue;
    }

    domainValues.min = Math.min(domainValues.min, currentDatapoint.value);
    domainValues.max = Math.max(domainValues.max, currentDatapoint.value);
    updateXAxisBounds(
      currentDatapoint,
      xAxisBounds,
      customXAxisMin,
      customXAxisMax,
    );

    sanitizedDatapoints.push(currentDatapoint);

    if (gapPolicy === 'gap') {
      processGap(series.datapoints, i, currentDatapoint, sanitizedDatapoints);
    }
  }

  addXAxisBoundaryDatapoints(
    sanitizedDatapoints,
    xAxis,
    xAxisBounds,
    customXAxisMin,
    customXAxisMax,
  );

  return {
    sanitizedSeries: { ...series, datapoints: sanitizedDatapoints },
    domainValues,
  };
};
